import { devLog } from '../../../../utils/devLog';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertTriangle, PlusCircle, Search, Wrench, Clock, Download, Loader2 } from 'lucide-react';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import { useVessel } from 'contexts/VesselContext';
import { useCrew } from '../../../../hooks/useCrew';
import { useDefectsData } from '../../../../hooks/domain/useDefectsData';
import { useCustomDialogs } from '../../../../hooks/useCustomDialogs';
import { getContext } from '../../../../lib/context';
import { Input } from '../../../catalyst/input';
import DefectsContent from './DefectsContent';
import DefectsSidebar from './DefectsSidebar';
import DefectsSummaryCard from './DefectsSummaryCard';
import DefectDetailView from './DefectDetailView';
import RecordDefectView from './RecordDefectView';
import LogRepairView from './LogRepairView';
import DefectsHistoryView from './DefectsHistoryView';
import DefectsSettingsView from './DefectsSettingsView';
import FilterTabs from '../../common/FilterTabs';
import TabNavigation from '../../TabNavigation';
import Dropdown from '../../../common/Dropdown';
import { addAuditEntry, AUDIT_ACTIONS, AUDIT_MODULES } from '../../../../utils/audit';
import { exportDefectsReportPDF } from '../../../../utils/pdfExport';
import { useMainLogbook } from '../../../../hooks/domain/operations/useMainLogbook';
import EditReasonModal from '../../../common/EditReasonModal';
import { calculateDefectStats } from './utils/defectHelpers';
// FT-209: Import maintenance jobs domain for corrective job creation
import { useMaintenanceJobsDomain } from '../../../../hooks/domain/maintenance/useMaintenanceJobsDomain';
import { useDefects } from '../../../../hooks/domain/defects/useDefects';
import { filterTombstoned } from '../../../../sync/tombstone';  // FT-458: hide soft-deleted defects from display/counts/export
// FT-357: shell-level back handler — register a "back to dashboard" handler
// so mobile users always see a Back button in the bottom bar.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

const DefectsModule = ({ setActiveModule, setActiveModuleWithTab }) => {
  // FT-357: register a shell-level "back to dashboard" handler so mobile
  // users always see a Back button in the bottom bar for this module.
  useSetBackHandler(
    useCallback(() => {
      if (setActiveModule) setActiveModule('dashboard');
    }, [setActiveModule])
  );


  const { currentVesselId, getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();

  // Main Logbook hook for auto-logging defects
  const { logDefect, logDefectResolved } = useMainLogbook();

  // Use the NEW useCrew hook for crew data
  const { crew, loading: crewLoading } = useCrew();
  
  // Use domain hook for defects data - base key only
  const {
    defects,
    setDefects: saveDefects,
    updateDefectStatus,
    getCriticalDefects,
    getOpenDefectsCount,
    getUrgentDefects
  } = useDefectsData();

  // FT-209: Maintenance jobs domain for corrective job creation
  // FT-353f: same domain hook exposes clearOriginatingDefectId for the defect-delete cascade
  const maintenanceJobsDomain = useMaintenanceJobsDomain();
  // FT-209: useDefects for updating defects with correctiveJobId linkage
  const { linkCorrectiveJob } = useDefects();
  
  devLog('[DefectsModule] Using domain hook - defects array:', defects);
  devLog('[DefectsModule] Total defects count:', defects?.length || 0);
  devLog('[DefectsModule] Defects from maintenance:', defects?.filter(d => d.source === 'maintenance').length || 0);
  devLog('[DefectsModule] Open defects:', defects?.filter(d => d.status === 'Open' || d.status === 'open').length || 0);
  devLog('[DefectsModule] Crew count:', crew?.length || 0);
  
  
  const { showAlert, AlertDialog } = useCustomDialogs();
  
  const [showRecordView, setShowRecordView] = useState(false);
  const [recordMode, setRecordMode] = useState('defect');
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [showDefectDetail, setShowDefectDetail] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Tab configuration - matches Drills module pattern
  const tabs = [
    { id: 'defects', label: 'Defects' },
    { id: 'history', label: 'Defect History' }
  ];
  const [activeTab, setActiveTab] = useState('defects');

  const [activeFilter, setActiveFilter] = useState('active'); // 'active', 'resolved', 'repairs'
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showRepairsOnly, setShowRepairsOnly] = useState(false); // Keep for backward compatibility
  const [prefillData, setPrefillData] = useState(null);

  // Edit reason modal state
  const [showEditReasonModal, setShowEditReasonModal] = useState(false);
  const [defectEditReason, setDefectEditReason] = useState('');

  // Loading states for async operations
  const [isSavingDefect, setIsSavingDefect] = useState(false);
  const [isUpdatingDefect, setIsUpdatingDefect] = useState(false);
  const [isDeletingDefect, setIsDeletingDefect] = useState(false);

  // FT-358: register middle-slot bottom-bar actions for the list view.
  // Sub-views (record / detail / settings) are early-returned below and
  // register their own actions; when this component is showing a sub-view
  // we pass an empty array so it doesn't override the sub-view's list.
  const isSubView = showRecordView || showDefectDetail || showSettings;
  useSetBottomBarActions(
    isSubView
      ? []
      : activeTab === 'history'
      ? [
          {
            key: 'defects-history-export',
            icon: Download,
            label: 'Export',
            // FT-458: export the display list (tombstones hidden), not raw.
            onClick: () => exportDefectsReportPDF(filterTombstoned(defects), currentVessel),
          },
        ]
      : [
          {
            key: 'defects-report',
            icon: PlusCircle,
            label: 'Report',
            primary: true,
            onClick: () => {
              setRecordMode('defect');
              setShowRecordView(true);
            },
          },
          {
            key: 'defects-history',
            icon: Clock,
            label: 'History',
            onClick: () => setActiveTab('history'),
          },
          {
            key: 'defects-export',
            icon: Download,
            label: 'Export',
            // FT-458: export the display list (tombstones hidden), not raw.
            onClick: () => exportDefectsReportPDF(filterTombstoned(defects), currentVessel),
          },
        ],
  );

  // PERFORMANCE MONITORING - Defects Optimization
  useEffect(() => {
    const startTime = performance.now();

    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (duration > 100) {

      }
    };
  });

  // Helper function to check and consume prefill data
  const checkForPrefillData = useCallback(() => {
    const moduleTab = sessionStorage.getItem('moduleTab_defects');

    if (moduleTab === 'record') {
      const prefillDataRaw = sessionStorage.getItem('defects:newPrefill');
      if (prefillDataRaw) {
        try {
          const parsed = JSON.parse(prefillDataRaw);

          // Validate payload according to DEFECTS_LINKING_SPEC
          if (parsed?.version === "1.0" &&
              parsed?.source === "equipment-register" &&
              parsed?.timestamp &&
              Date.now() - parsed.timestamp < 5 * 60 * 1000) { // 5 minute TTL

            setPrefillData(parsed);
            setShowRecordView(true);
            setRecordMode('defect');
            devLog('[DefectsModule] Applied prefill data from equipment:', parsed.equipmentName);
          }

          // Always remove sessionStorage after read (one-shot consumption)
          sessionStorage.removeItem('defects:newPrefill');
          sessionStorage.removeItem('moduleTab_defects');
        } catch (error) {
          devLog('[DefectsModule] Error parsing prefill data:', error);
          sessionStorage.removeItem('defects:newPrefill');
          sessionStorage.removeItem('moduleTab_defects');
        }
      }
    }
  }, []);

  // Check for equipment prefill data when component mounts
  useEffect(() => {
    checkForPrefillData();
  }, [checkForPrefillData]);

  // Listen for defects:checkPrefill event (dispatched when navigating from equipment)
  useEffect(() => {
    const handleCheckPrefill = () => {
      devLog('[DefectsModule] Received defects:checkPrefill event');
      checkForPrefillData();
    };

    window.addEventListener('defects:checkPrefill', handleCheckPrefill);
    return () => window.removeEventListener('defects:checkPrefill', handleCheckPrefill);
  }, [checkForPrefillData]);

  // FT-458: display/count/export chokepoint — filter out soft-deleted (tombstoned)
  // defects. `defects` (raw, tombstones intact) is still used for all WRITES
  // (save/delete/update) so the tombstone is preserved + propagates; only this
  // derived list feeds the UI, stats and the PDF export.
  const displayDefects = filterTombstoned(defects);

  // Handle tab navigation from dashboard
  // TODO: Re-enable when targetTab is passed as prop
  // useEffect(() => {
  //   if (targetTab && ['active', 'resolved'].includes(targetTab)) {
  //     setActiveFilter(targetTab);
  //     clearTargetTab();
  //   }
  // }, [targetTab, clearTargetTab]);
  
  // Sync showRepairsOnly with activeFilter
  useEffect(() => {
    if (activeFilter === 'repairs') {
      setShowRepairsOnly(true);
    } else {
      setShowRepairsOnly(false);
    }
  }, [activeFilter]);

  // Calculate stats - memoized for performance
  // FT-90: Added High/Medium/Low counts for penalty-based compliance calculation
  // FT-180: Uses shared calculateDefectStats() - single source of truth with Dashboard
  const stats = useMemo(() => {
    const baseStats = calculateDefectStats(displayDefects);
    // Add recentActivity for module-specific needs (not in shared function)
    return {
      ...baseStats,
      recentActivity: []
    };
  }, [displayDefects]);

  // Handle saving new defect with initial audit trail
  const handleSaveDefect = async (defectRecord) => {
    setIsSavingDefect(true);
    try {
      // Get current user for audit trail
      const userName = getContext().userName;
      const vesselName = currentVessel?.name || 'Unknown Vessel';

      // Create initial audit entry for the defect
      const initialAuditEntry = {
        id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        timestamp: new Date().toISOString(),
        user: userName,
        vessel: vesselName,
        action: 'created',
        summary: `${defectRecord.record_type === 'repair' ? 'Repair record' : 'Defect'} created`,
        changes: [],
        reason: 'Initial record creation'
      };

      // Add audit trail to the defect record
      const defectWithAudit = {
        ...defectRecord,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        auditLog: [initialAuditEntry]
      };

      const updatedDefects = [...(defects || []), defectWithAudit];
      saveDefects(updatedDefects);

      // Also add to global audit log
      addAuditEntry({
        action: AUDIT_ACTIONS.CREATE,
        module: AUDIT_MODULES.DEFECT,
        targetId: defectRecord.id,
        summary: `${defectRecord.record_type === 'repair' ? 'Logged repair' : 'Created defect'}: ${defectRecord.description.substring(0, 50)}${defectRecord.description.length > 50 ? '...' : ''}`,
        details: {
          type: defectRecord.record_type,
          riskLevel: defectRecord.risk_level,
          status: defectRecord.status,
          assignedTo: defectRecord.assigned_to,
          equipmentLinked: defectRecord.equipment_linked
        },
        context: {
          vesselName: currentVessel?.name,
          category: currentVessel?.category
        }
      });

      // Auto-log new defects to Official Logbook (not repairs)
      if (defectRecord.record_type !== 'repair') {
        await logDefect({
          defectId: defectRecord.id,
          title: defectRecord.description.substring(0, 100),
          description: defectRecord.description,
          priority: defectRecord.risk_level,
          category: defectRecord.category,
          location: defectRecord.location,
          raisedAt: new Date().toISOString(),
          timestamp: new Date().toISOString()
        });
      }

      // Clear prefill data and return to defects list
      setPrefillData(null);
      setShowRecordView(false);
      setSelectedDefect(null);

      // Clear any navigation origin context since we've successfully saved
      // This ensures that subsequent back navigation doesn't try to go back to equipment
      sessionStorage.removeItem('nav:origin');
    } catch (error) {

    } finally {
      setIsSavingDefect(false);
    }
  };

  // Handle updating defect with field-level audit trail
  const handleUpdateDefect = async (updatedDefect) => {
    if (selectedDefect?.status === 'Closed' && selectedDefect?.record_type !== 'repair') {
      showAlert(
        'Compliance Restriction',
        'Cannot modify closed defects - WBC3 audit compliance requirement',
        'warning'
      );
      return;
    }

    setIsUpdatingDefect(true);
    try {
      // Get current user for audit
      const userName = getContext().userName;
      const vesselName = currentVessel?.name || 'Unknown Vessel';

      // Track field-level changes for audit trail
      const fieldsToTrack = [
        'description', 'risk_level', 'status', 'assigned_to', 'equipment_linked',
        'location', 'category', 'target_date', 'notes', 'corrective_action'
      ];

      const changes = [];
      fieldsToTrack.forEach(field => {
        const oldValue = selectedDefect?.[field];
        const newValue = updatedDefect[field];
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          changes.push({
            field,
            oldValue: oldValue ?? '',
            newValue: newValue ?? ''
          });
        }
      });

      // Generate summary of changes
      const changedFields = changes.map(c => c.field.replace(/_/g, ' ')).join(', ');
      const summary = changes.length > 0
        ? `Updated ${changedFields}`
        : 'Record updated (no field changes detected)';

      // Create audit entry for the defect's internal audit log
      const auditEntry = {
        id: `audit_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        timestamp: new Date().toISOString(),
        user: userName,
        vessel: vesselName,
        action: 'updated',
        summary,
        changes,
        reason: defectEditReason || 'Record updated'
      };

      // Build updated defect with audit trail
      const defectWithAudit = {
        ...updatedDefect,
        updatedAt: new Date().toISOString(),
        auditLog: [...(selectedDefect?.auditLog || []), auditEntry]
      };

      const updatedDefects = (defects || []).map(defect =>
        defect.id === defectWithAudit.id ? defectWithAudit : defect
      );

      saveDefects(updatedDefects);

      // Also add to global audit log
      addAuditEntry({
        action: AUDIT_ACTIONS.UPDATE,
        module: AUDIT_MODULES.DEFECT,
        targetId: updatedDefect.id,
        summary: `Updated ${updatedDefect.record_type === 'repair' ? 'repair' : 'defect'}: ${updatedDefect.description.substring(0, 50)}`,
        details: {
          status: updatedDefect.status,
          previousStatus: selectedDefect.status,
          reason: defectEditReason,
          changesCount: changes.length
        }
      });

      // Auto-log defect resolution to Official Logbook when status changes to Closed
      if (updatedDefect.status === 'Closed' && selectedDefect.status !== 'Closed' && updatedDefect.record_type !== 'repair') {
        const daysOpen = Math.ceil((new Date() - new Date(selectedDefect.createdAt)) / (1000 * 60 * 60 * 24));
        await logDefectResolved({
          defectId: updatedDefect.id,
          title: updatedDefect.description.substring(0, 100),
          description: updatedDefect.description,
          resolution: updatedDefect.corrective_action || 'Resolved',
          resolvedBy: userName,
          priority: updatedDefect.risk_level,
          daysOpen,
          resolvedAt: new Date().toISOString(),
          timestamp: new Date().toISOString()
        });
      }

      // Clear edit reason state
      setDefectEditReason('');
      setShowDefectDetail(false);
      setSelectedDefect(null);
    } catch (error) {

    } finally {
      setIsUpdatingDefect(false);
    }
  };

  // Handle deleting defect
  const handleDeleteDefect = async (defectToDelete) => {
    setIsDeletingDefect(true);
    try {
      // FT-353f: cascade — clear originatingDefectId on the linked job so
      // the job stops advertising a stale back-reference. Job is NOT
      // deleted (audit trail preservation) and its jobType='corrective'
      // is left intact (the corrective nature was true at creation).
      const linkedJobId = typeof defectToDelete.correctiveJobId === 'string'
        ? defectToDelete.correctiveJobId
        : defectToDelete.correctiveJobId?.id || null;
      if (linkedJobId) {
        try {
          maintenanceJobsDomain.clearOriginatingDefectId(linkedJobId);
          devLog('[DefectsModule] FT-353f: cleared originatingDefectId on job', linkedJobId);
        } catch (cascadeErr) {
          devLog('[DefectsModule] FT-353f: cascade cleanup failed (non-fatal):', cascadeErr);
        }
      }

      // FT-458 SAFETY-CRITICAL: soft-delete via tombstone instead of hard-filter.
      // A hard `filter(...)` DROPS the item from the array; the cloud∪local sync
      // union has no set-difference, so the still-present cloud copy re-seeds it
      // and the delete resurrects. We STAMP `deleted_at` (+ `deleted_by`) and
      // KEEP the item in the array (mirrors shipped FT-175 crew), so the deletion
      // propagates to cloud + other devices and is filtered out of every read.
      const nowIso = new Date().toISOString();
      const deletedBy = getContext().userName || 'Unknown';
      const updatedDefects = (defects || []).map(defect =>
        defect.id === defectToDelete.id
          ? { ...defect, deleted_at: nowIso, deleted_by: deletedBy, updatedAt: Date.now() }
          : defect
      );
      saveDefects(updatedDefects);

      addAuditEntry({
        action: AUDIT_ACTIONS.DELETE,
        module: AUDIT_MODULES.DEFECT,
        targetId: defectToDelete.id,
        summary: `Deleted ${defectToDelete.record_type === 'repair' ? 'repair' : 'defect'}: ${defectToDelete.description.substring(0, 50)}`
      });

      setShowDefectDetail(false);
      setSelectedDefect(null);
    } catch (error) {

    } finally {
      setIsDeletingDefect(false);
    }
  };

  // Handle editing existing defect - show edit reason modal first
  const handleEditDefect = () => {
    // Check if defect is closed (compliance restriction)
    if (selectedDefect?.status === 'Closed' && selectedDefect?.record_type !== 'repair') {
      showAlert(
        'Compliance Restriction',
        'Cannot modify closed defects - WBC3 audit compliance requirement',
        'warning'
      );
      return;
    }
    // Show edit reason modal before editing
    setShowEditReasonModal(true);
  };

  // Handle edit reason confirmation - proceed to edit form
  const handleEditReasonConfirm = (reason) => {
    setDefectEditReason(reason);
    setShowEditReasonModal(false);
    // Determine the mode based on the defect type
    setRecordMode(selectedDefect?.record_type === 'repair' ? 'repair' : 'defect');
    setShowRecordView(true);
    setShowDefectDetail(false);
  };

  // FT-209: Handle generating corrective job from defect
  const handleGenerateCorrectiveJob = async (newJob, defect) => {
    try {
      devLog('[DefectsModule] FT-209: Generating corrective job for defect:', defect.id);

      // 1. Add the job via maintenance jobs domain
      // FT-209 FIX: add() returns the full job object, not just the ID
      const addedJob = maintenanceJobsDomain.add(newJob);
      const savedJobId = addedJob?.id || newJob.id;
      devLog('[DefectsModule] FT-209: Created corrective job:', savedJobId);

      // 2. Link the job to the defect
      const userName = getContext().userName || 'Unknown';
      linkCorrectiveJob(defect.id, savedJobId, userName);
      devLog('[DefectsModule] FT-209: Linked job to defect');

      // 3. Update the selected defect in state so UI reflects the change
      setSelectedDefect(prev => ({
        ...prev,
        correctiveJobId: savedJobId,
        status: 'in progress'
      }));

      // 4. Add audit entry
      addAuditEntry({
        action: AUDIT_ACTIONS.CREATE,
        module: AUDIT_MODULES.MAINTENANCE,
        targetId: savedJobId,
        summary: `Created corrective job from defect ${defect.id}: ${newJob.name}`
      });

      devLog('[DefectsModule] FT-209: Corrective job generation complete');
    } catch (error) {
      console.error('[DefectsModule] FT-209: Error generating corrective job:', error);
      showAlert('Error', 'Failed to generate corrective job. Please try again.', 'error');
    }
  };

  // FT-209: Navigate to maintenance module to view a job
  const handleNavigateToJob = (jobId) => {
    devLog('[DefectsModule] FT-209: Navigating to job:', jobId);
    // Use setActiveModuleWithTab if available, otherwise setActiveModule
    if (setActiveModuleWithTab) {
      setActiveModuleWithTab('maintenance', 'planned', { highlightJobId: jobId });
    } else if (setActiveModule) {
      setActiveModule('maintenance');
    }
  };

  // Handle quick actions from sidebar
  const handleQuickAction = (action) => {
    switch (action) {
      case 'report':
        setRecordMode('defect');
        setShowRecordView(true);
        break;
      case 'repair':
        setRecordMode('repair');
        setShowRecordView(true);
        break;
      case 'history':
        setActiveTab('history');
        break;
      case 'export':
        // FT-458: exclude soft-deleted defects from the exported PDF, matching
        // the two bottom-bar export actions (see the filterTombstoned siblings).
        exportDefectsReportPDF(filterTombstoned(defects), currentVessel);
        break;
      case 'settings':
        setShowSettings(true);
        break;
      default:
        break;
    }
  };

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'defects':
        // Active defects view (existing main content)
        return (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content - 2/3 width */}
            <div className="lg:col-span-2 pb-20 lg:pb-0">
              <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
                {/* Main Header with Search */}
                <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
                  <div className="flex items-center justify-between gap-6">
                    {/* Title - Responsive size */}
                    <h3 className="text-base font-semibold text-white whitespace-nowrap flex-shrink-0">
                      Active Defects<span className="hidden sm:inline"> & Repairs</span>
                    </h3>

                    {/* Search Input */}
                    <div className="relative flex-1 max-w-2xl">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search defects..."
                        aria-label="Search defects by equipment or description"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-white text-slate-800 text-sm border border-white/20 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
                      />
                    </div>
                  </div>
                </div>

                {/* Filter Section */}
                <div className="px-6 py-5 space-y-3">
                  {/* Row 1: Category Filter Dropdown */}
                  <div className="flex gap-3">
                    <Dropdown
                      value={categoryFilter}
                      onChange={setCategoryFilter}
                      width="flex-1 sm:flex-none sm:w-44"
                      options={[
                        { value: 'all', label: 'All Categories' },
                        { value: 'lsa', label: 'LSA' },
                        { value: 'ffe', label: 'FFE' },
                        { value: 'machinery', label: 'Machinery' },
                        { value: 'navigation', label: 'Navigation' },
                        { value: 'medical', label: 'Medical' },
                        { value: 'gmdss', label: 'GMDSS' },
                        { value: 'electrical', label: 'Electrical' },
                        { value: 'environmental', label: 'Environmental' },
                        { value: 'operations', label: 'Operations' },
                        { value: 'other', label: 'Other' },
                      ]}
                    />
                  </div>

                  {/* Row 2: FilterTabs (keeps active/repairs filter within Defects tab) */}
                  <div className="flex items-center justify-between">
                    <FilterTabs
                      tabs={[
                        { id: 'active', label: 'Active', count: stats.active },
                        { id: 'repairs', label: 'Repairs Only' }
                      ]}
                      activeTab={activeFilter}
                      onTabChange={setActiveFilter}
                    />
                    <span className="hidden sm:inline text-sm text-gray-500">{stats.active} active</span>
                  </div>
                </div>

                {/* Content area */}
                <div className="p-6">
                  <DefectsContent
                    defects={displayDefects || []}
                    activeTab={activeFilter === 'repairs' ? 'active' : 'active'}
                    searchTerm={searchTerm}
                    categoryFilter={categoryFilter}
                    onSearchChange={setSearchTerm}
                    onRecordDefect={() => {
                      setRecordMode('defect');
                      setShowRecordView(true);
                    }}
                    onViewDefect={(defect) => {
                      setSelectedDefect(defect);
                      setShowDefectDetail(true);
                    }}
                    showRepairsOnly={activeFilter === 'repairs'}
                    onToggleRepairsOnly={setShowRepairsOnly}
                  />
                </div>
              </div>
            </div>

            {/* Sidebar - 1/3 width - Hidden on mobile */}
            <div className="hidden lg:block">
              <DefectsSidebar
                stats={stats}
                onQuickAction={handleQuickAction}
              />
            </div>
          </div>
        );

      case 'history':
        // Defect history view - full report-style view with cards
        return (
          <DefectsHistoryView
            onBack={() => setActiveTab('defects')}
          />
        );

      default:
        return null;
    }
  };

  // Show full-screen record view if active - Defect mode
  if (showRecordView && recordMode === 'defect') {
    return (
      <RecordDefectView
        onBack={() => {
          // Check for return context (from Equipment Detail "Log Defect" flow)
          const returnContextRaw = sessionStorage.getItem('defectReturnContext');

          if (returnContextRaw) {
            try {
              const returnContext = JSON.parse(returnContextRaw);

              // Check if we should return to Equipment Detail
              if (returnContext.returnModule === 'equipment' &&
                  returnContext.returnView === 'detail' &&
                  returnContext.equipmentId) {

                devLog('[DefectsModule] Returning to equipment detail:', returnContext.equipmentId);

                // Store the equipment ID to re-open detail view
                sessionStorage.setItem('reopenEquipmentDetail', returnContext.equipmentId);

                // Clear return context
                sessionStorage.removeItem('defectReturnContext');
                sessionStorage.removeItem('selectedEquipmentForDefect');
                setPrefillData(null);

                // Navigate back to Equipment module (which is within Maintenance)
                // Switch tab back to equipment (Tab ID, not source identifier)
                sessionStorage.setItem('moduleTab_maintenance', 'equipment');
                window.dispatchEvent(new CustomEvent('maintenance:switchTab', { detail: { tab: 'equipment' } }));

                // Close record view
                setShowRecordView(false);
                setRecordMode(null);
                setSelectedDefect(null);

                return;
              }
            } catch (error) {
              devLog('[DefectsModule] Error parsing return context:', error);
            }

            // Clean up stale return context
            sessionStorage.removeItem('defectReturnContext');
          }

          // Default behavior: close the record view
          setShowRecordView(false);
          setRecordMode(null);
          setSelectedDefect(null);
          setPrefillData(null);
        }}
        onSave={handleSaveDefect}
        initialData={selectedDefect}
        prefillData={prefillData}
        isSaving={isSavingDefect}
      />
    );
  }

  // Show full-screen record view if active - Repair mode
  if (showRecordView && recordMode === 'repair') {
    return (
      <LogRepairView
        onBack={() => {
          setShowRecordView(false);
          setRecordMode(null);
          setSelectedDefect(null);
        }}
        onSave={handleSaveDefect}
        initialData={selectedDefect}
        isSaving={isSavingDefect}
      />
    );
  }

  // Show full-screen detail view if viewing a defect
  if (showDefectDetail && selectedDefect) {
    return (
      <>
        <DefectDetailView
          defect={selectedDefect}
          onBack={() => {
            setShowDefectDetail(false);
            setSelectedDefect(null);
            setShowEditReasonModal(false); // Clear modal state too
          }}
          onEdit={handleEditDefect}
          onDelete={handleDeleteDefect}
          onStatusChange={handleUpdateDefect}
          crewList={crew}
          isUpdating={isUpdatingDefect}
          isDeleting={isDeletingDefect}
          // FT-209: Props for corrective job creation
          onGenerateCorrectiveJob={handleGenerateCorrectiveJob}
          onNavigateToJob={handleNavigateToJob}
          maintenanceJobs={maintenanceJobsDomain.allJobs}
        />

        {/* Edit Reason Modal - MCA Compliance - Available in detail view */}
        <EditReasonModal
          isOpen={showEditReasonModal}
          onClose={() => setShowEditReasonModal(false)}
          onConfirm={handleEditReasonConfirm}
          item={selectedDefect}
          recordType={selectedDefect?.record_type === 'repair' ? 'repair record' : 'defect'}
        />
      </>
    );
  }

  // Show full-screen settings view if active
  if (showSettings) {
    return (
      <DefectsSettingsView
        onBack={() => setShowSettings(false)}
      />
    );
  }

  return (
    <>
      {/* Module Title - matches Equipment and Drills pattern */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Defects</h1>
        <p className="mt-1 text-sm text-gray-500">Manage vessel defects, repairs, and corrective actions</p>
      </div>

      {/* Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      <div className="mt-6">
        {renderTabContent()}
      </div>

      {/* Mobile Summary Box - ONLY visible on mobile (<lg) */}
      <div className="lg:hidden mb-20 px-4">
        <DefectsSummaryCard stats={stats} />
      </div>

      {/* FT-358: mobile bottom-bar actions registered via
          useSetBottomBarActions above — the shell owns the bar. */}

      {/* Custom Alert Dialog */}
      <AlertDialog />

      {/* Edit Reason Modal - MCA Compliance */}
      <EditReasonModal
        isOpen={showEditReasonModal}
        onClose={() => setShowEditReasonModal(false)}
        onConfirm={handleEditReasonConfirm}
        item={selectedDefect}
        recordType={selectedDefect?.record_type === 'repair' ? 'repair record' : 'defect'}
      />
    </>
  );
};

export default DefectsModule;